import React, { useState } from 'react';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';

import { UiFilterTextField } from '../../ui/ui-filter-text-field';
import { UiFilterToggle } from '../../ui/ui-filter-toggle';

import { formatDate, formatPhoneNumber } from '../../util/formatters';
import { sortAlphabetically, SORT_ORDER } from '../../util/sort-functions';
import { OrgSelect } from '../../widgets/org-select';
import { TableHeaderCell } from '../../widgets/table-components/table-header-cell';

type Props = {
  originOrgId: number;
  employeesForGroupId: (groupId: number) => Employee[];
  employeeById: (employeeId: number) => Employee;
  orgs: Org[];
  groupReassignments: { [employeeGroupId: number]: number };
  explicitEmployeeAssignments: { [employeeId: number]: number };
};

export const PreviewImportTab = ({
  originOrgId,
  employeesForGroupId,
  employeeById,
  orgs,
  groupReassignments,
  explicitEmployeeAssignments,
}: Props) => {
  const pageSize = 100;
  const [employeePreviewOffset, setEmployeePreviewOffset] = useState<number>(0);
  const [previewImportOrgId, setPreviewImportOrgId] = useState<number>(originOrgId);
  const [employeeNameFilter, setEmployeeNameFilter] = useState<string>('');
  const [shouldSortEmployeesByLastName, setShouldSortEmployeesByLastName] = useState<boolean>(true);

  const handlePreviewImportOrgIdChange = (
    event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>,
  ) => {
    setPreviewImportOrgId(event.target.value as number);
  };

  const handleEmployeeNameFilterChange = (updatedEmployeeNameFilter: string) => {
    setEmployeeNameFilter(updatedEmployeeNameFilter);
  };

  const handleShouldSortEmployeesByLastNameChange = () => {
    setShouldSortEmployeesByLastName(!shouldSortEmployeesByLastName);
  };

  const handleEmployeePreviewPageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    setEmployeePreviewOffset(page * pageSize);
  };

  const getEmployeesForOrg = (orgId: number) => {
    const employeesById: { [employeeId: number]: Employee } = {};
    Object.entries(groupReassignments).forEach(([groupId, reassignedOrgId]) => {
      if (reassignedOrgId !== orgId) {
        return;
      }
      employeesForGroupId(parseInt(groupId, 10)).forEach((employee) => {
        if (
          explicitEmployeeAssignments[employee.employeeId] &&
          explicitEmployeeAssignments[employee.employeeId] !== orgId
        ) {
          return;
        }

        employeesById[employee.employeeId] = employee;
      });
    });

    Object.entries(explicitEmployeeAssignments).forEach(([employeeIdString, reassignedOrgId]) => {
      if (reassignedOrgId !== orgId) {
        return;
      }

      const employeeId = parseInt(employeeIdString, 10);
      employeesById[employeeId] = employeeById(parseInt(employeeIdString, 10));
    });

    return Object.values(employeesById);
  };

  const employeesInOrg = getEmployeesForOrg(previewImportOrgId)
    .filter((employee) =>
      `${employee.Contact.Person.firstName} ${employee.Contact.Person.lastName}`.match(
        new RegExp(employeeNameFilter, 'i'),
      ),
    )
    .sort(
      sortAlphabetically(
        (employee) =>
          shouldSortEmployeesByLastName
            ? employee.Contact.Person.lastName
            : employee.Contact.Person.firstName,
        SORT_ORDER.ASC,
      ),
    );
  const paginatedEmployeesInOrg = employeesInOrg.slice(
    employeePreviewOffset,
    employeePreviewOffset + pageSize,
  );

  return (
    <div className="card-head-no-border typo-subtitle u-margin-top">
      <OrgSelect
        className="u-width-20 u-margin-right"
        disableUnderline
        onChange={handlePreviewImportOrgIdChange}
        orgOptions={orgs}
        value={previewImportOrgId}
      />
      <div className="card">
        <div className="card-body u-display-flex u-alignItems-flexStart">
          <UiFilterTextField
            label="Employee Name"
            value={employeeNameFilter}
            onChange={handleEmployeeNameFilterChange}
          />
          <UiFilterToggle
            className="u-margin-left"
            label={`Sort by ${shouldSortEmployeesByLastName ? 'First' : 'Last'} Name`}
            value={shouldSortEmployeesByLastName}
            onChange={handleShouldSortEmployeesByLastNameChange}
          />
        </div>
      </div>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableHeaderCell>First Name</TableHeaderCell>
            <TableHeaderCell>Last Name</TableHeaderCell>
            <TableHeaderCell>Email</TableHeaderCell>
            <TableHeaderCell>Phone Number</TableHeaderCell>
            <TableHeaderCell>Birthdate</TableHeaderCell>
            <TableHeaderCell>Start Date</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedEmployeesInOrg.map((employee) => (
            <TableRow key={employee.employeeId}>
              <TableCell>{employee.Contact.Person.firstName}</TableCell>
              <TableCell>{employee.Contact.Person.lastName}</TableCell>
              <TableCell>{employee.Contact.Person.email || ''}</TableCell>
              <TableCell>{formatPhoneNumber(employee.Contact.Person.phoneNumber) || ''}</TableCell>
              <TableCell>{formatDate(employee.Contact.Person.birthdate) || ''}</TableCell>
              <TableCell>{formatDate(employee.startDate) || ''}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={employeesInOrg.length}
        rowsPerPage={pageSize}
        page={employeePreviewOffset / pageSize}
        backIconButtonProps={{
          'aria-label': 'Previous Page',
        }}
        nextIconButtonProps={{
          'aria-label': 'Next Page',
        }}
        onPageChange={handleEmployeePreviewPageChange}
        rowsPerPageOptions={[pageSize]}
      />
    </div>
  );
};
